import { useCallback, useEffect, useState } from 'react';
import { Package, Receipt, Truck, Loader2 } from 'lucide-react';
import ModalShell from '../../components/ui/ModalShell';
import UctenkaModal from '../RozvozCockpit/UctenkaModal';
import { AKCENT, kc, ocekavanaPlatba } from '../RozvozCockpit/ui';

/**
 * Přehled rozvozů — všechny dnešní nedoručené objednávky na rozvoz, ať jsou
 * kdekoli: přijatá, v kuchyni, hotová v teple, u rozvozce, na cestě
 * (přání rozvozců 2026-10-22). Jen čtení; lístky stejné jako K odvozu,
 * u každého účtenka jako náhled (bez přidělení čísla dokladu).
 */
export const STAVY = {
    na_ceste:   { text: 'Na cestě',      barva: '#2563EB' },
    u_rozvozce: { text: 'U rozvozce',    barva: '#975ACD' },
    hotova:     { text: 'Hotová v teple', barva: AKCENT },
    v_kuchyni:  { text: 'V kuchyni',     barva: '#D97706' },
    prijata:    { text: 'Před kuchyní',  barva: '#6B7280' },
};

/** Data přehledu; `null` = rozvozce to nevidí (endpoint 403) → tlačítko se neukáže. */
export function usePrehledRozvozu({ enabled = true, interval = 30000 } = {}) {
    const [objednavky, setObjednavky] = useState(null);
    const nacti = useCallback(async () => {
        try {
            const r = await fetch('/api/rozvoz/prehled', { credentials: 'same-origin' });
            if (!r.ok) { if (r.status === 403) setObjednavky(null); return; }
            setObjednavky((await r.json()).objednavky ?? []);
        } catch { /* bez spojení — zkusí se příště */ }
    }, []);
    useEffect(() => {
        if (!enabled) return undefined;
        nacti();
        const id = setInterval(() => { if (!document.hidden) nacti(); }, interval);
        return () => clearInterval(id);
    }, [enabled, interval, nacti]);
    return { objednavky, nacti };
}

export default function PrehledRozvozu({ objednavky, onObnovit, onClose, isDark }) {
    const [uctenka, setUctenka] = useState(null);

    // Při otevření čerstvá data, pak co 10 s — přehled se čte, když se něco hýbe
    useEffect(() => {
        onObnovit?.();
        const id = setInterval(() => onObnovit?.(), 10000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ModalShell open onClose={onClose} isDark={isDark} nadpis="Rozvozy v systému" ikona={Truck} barva={AKCENT}>
            <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {objednavky == null && (
                    <div style={{ textAlign: 'center', padding: 24 }}><Loader2 size={20} className="animate-spin" style={{ color: AKCENT }} /></div>
                )}
                {objednavky?.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 24, fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>
                        Žádný rozvoz teď v systému není.
                    </div>
                )}
                {objednavky?.map((o, i) => (
                    <div key={o.id} style={{ display: 'contents' }}>
                        {(i === 0 || objednavky[i - 1].stav !== o.stav) && (
                            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: STAVY[o.stav]?.barva, marginTop: i ? 6 : 0 }}>
                                {STAVY[o.stav]?.text} · {objednavky.filter(x => x.stav === o.stav).length}
                            </div>
                        )}
                        <Listek o={o} onUctenka={() => setUctenka(o)} />
                    </div>
                ))}
            </div>
            {uctenka && (
                <UctenkaModal
                    zastavka={{ objednavka_id: uctenka.id }}
                    url={`/api/rozvoz/objednavky/${uctenka.id}/uctenka`}
                    onClose={() => setUctenka(null)} isDark={isDark}
                />
            )}
        </ModalShell>
    );
}

function Listek({ o, onUctenka }) {
    const stav = STAVY[o.stav] ?? STAVY.prijata;
    const platba = o.zaplaceno
        ? { text: 'zaplaceno', barva: 'var(--papir-ok)', pozadi: 'rgba(4,120,87,0.10)' }
        : ocekavanaPlatba(o) === 'kartou'
            ? { text: `kartou ${kc(o.castka_kc)}`, barva: 'var(--papir-info)', pozadi: 'rgba(29,78,216,0.10)' }
            : { text: `hotově ${kc(o.castka_kc)}`, barva: 'var(--brand)', pozadi: 'rgba(163,75,0,0.10)' };
    const lezi = o.stav === 'hotova' && o.hotovo_v ? Math.max(0, Math.round((Date.now() - new Date(o.hotovo_v).getTime()) / 60000)) : null;

    return (
        <div className="papir listek" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 14, borderLeft: `5px solid ${stav.barva}` }}>
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 9, minWidth: 0 }}>
                    <span style={{ fontFamily: 'var(--pismo-display)', fontSize: 26, lineHeight: 1, flexShrink: 0 }}>#{o.cislo}</span>
                    <span style={{ fontSize: 15.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.adresa || 'bez adresy'}</span>
                </span>
                <span style={{ display: 'flex', gap: 6, marginTop: 4, fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                    {/* Před kuchyní ukazujeme stav z Objedname (potvrzená, odložená…) */}
                    <b style={{ color: stav.barva }}>{o.stav === 'prijata' && o.stav_objednavky ? o.stav_objednavky.charAt(0).toUpperCase() + o.stav_objednavky.slice(1) : stav.text}</b>
                    {o.rozvozce && <span>· {o.rozvozce}</span>}
                    {lezi != null && <span>· leží <b style={{ color: 'var(--text-primary)' }}>{lezi} min</b></span>}
                    {o.cas_slibeny && <span>· slíbeno <b style={{ color: 'var(--text-primary)' }}>{String(o.cas_slibeny).slice(0, 5)}</b></span>}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, padding: '3px 8px', borderRadius: 7, background: platba.pozadi, color: platba.barva, whiteSpace: 'nowrap' }}>{platba.text}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)' }}>
                        <Package size={13} />{Math.max(1, o.krabic || 0)}
                    </span>
                </span>
            </span>
            <button type="button" onClick={onUctenka} aria-label={`Účtenka #${o.cislo}`} title="Účtenka" style={{
                width: 52, minHeight: 52, borderRadius: 13, flexShrink: 0,
                border: '2px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
            }}><Receipt size={20} /></button>
        </div>
    );
}
